import React from 'react';
import { CardViewContentProps } from './types';
import { Box, Typography } from '@mui/material';
import CategoryCard from '../../../../common/category-card/CategoryCard';
import CategoryCardGrid from '../../../../common/category-card/CategoryCardGrid';
import { CATEGORY_CARD_ACCENTS, CategoryCardTab } from '../../../../common/category-card/types';
import ApartmentOutlinedIcon from '../../../../../assets/svg/apartment.svg?react';
import BadgeOutlinedIcon from '../../../../../assets/svg/badge-light.svg?react';
import SupportAgentOutlinedIcon from '../../../../../assets/svg/support-agent.svg?react';
import BusinessCenterIcon from '../../../../../assets/svg/business-center-v2.svg?react';
import { useTranslation } from 'react-i18next';
import LibraryList from '../library-list/LibraryList';
import styles from '../../scss/Library.module.scss';
import { BusinessCategoryContent } from './BusinessCategoryContent';
import { EmploymentCategoryContent } from './EmploymentCategoryContent';
import { CustomersCategoryContent } from './CustomersCategoryContent';
import { SuppliersCategoryContent } from './SuppliersCategoryContent';
import PreviewBusiness from '../card-previews/preview-business/Preview';
import PreviewEmployment from '../card-previews/preview-employment/Preview';
import PreviewCustomers from '../card-previews/preview-customers/Preview';
import PreviewSuppliers from '../card-previews/preview-suppliers/Preview';

type LibraryCategoryConfig = {
  tab: CategoryCardTab;
  category: string;
  icon: React.ReactNode;
  preview: React.ReactNode;
};

const libraryCategories: LibraryCategoryConfig[] = [
  {
    tab: 'Business',
    category: 'business',
    icon: <ApartmentOutlinedIcon id='libraryCardViewContentBusinessCardApartmentIcon' />,
    preview: <PreviewBusiness />,
  },
  {
    tab: 'Employment',
    category: 'employment',
    icon: <BadgeOutlinedIcon id='libraryCardViewContentEmploymentCardBadgeIcon' />,
    preview: <PreviewEmployment />,
  },
  {
    tab: 'Customers',
    category: 'customers',
    icon: <SupportAgentOutlinedIcon id='libraryCardViewContentCustomersCardSupportAgentIcon' />,
    preview: <PreviewCustomers />,
  },
  {
    tab: 'Suppliers',
    category: 'suppliers',
    icon: <BusinessCenterIcon id='libraryCardViewContentSuppliersCardBusinessCenterIcon' />,
    preview: <PreviewSuppliers />,
  },
];

export const CardViewContent = ({
  activeTab,
  changeTabFunction,
  templates,
  workflow,
  setWorkflow,
}: CardViewContentProps) => {
  const { t } = useTranslation();

  const getTemplatesCountByCategory = (tab: CategoryCardTab) =>
    templates.filter((template) => template.category === tab).length;

  const getCountLabel = (count: number) =>
    t(count === 1 ? 'categoryCards.templateCountSingular' : 'categoryCards.templateCountPlural', {
      count,
    });

  if (activeTab === 'All') {
    return (
      <CategoryCardGrid id='libraryCardViewContentGrid'>
        {libraryCategories.map(({ tab, category, icon, preview }) => (
          <CategoryCard
            key={category}
            id={`libraryCardViewContent${tab}Card`}
            title={t(tab)}
            countLabel={getCountLabel(getTemplatesCountByCategory(tab))}
            description={t(`categoryCards.descriptions.${category}`)}
            accent={CATEGORY_CARD_ACCENTS[tab]}
            icon={icon}
            onClick={() => changeTabFunction(tab)}
            previewBlock={preview}
          />
        ))}
      </CategoryCardGrid>
    );
  }
  if (!workflow) {
    if (activeTab === 'Business') {
      return <BusinessCategoryContent templates={templates} setWorkflow={setWorkflow} />;
    } else if (activeTab === 'Employment') {
      return <EmploymentCategoryContent templates={templates} setWorkflow={setWorkflow} />;
    } else if (activeTab === 'Customers') {
      return <CustomersCategoryContent templates={templates} setWorkflow={setWorkflow} />;
    } else if (activeTab === 'Suppliers') {
      return <SuppliersCategoryContent templates={templates} setWorkflow={setWorkflow} />;
    }
  } else {
    return (
      <>
        <Box className={styles.breadcrumbs}>
          <Typography
            id='libraryCardViewContentWorkflowsLink'
            variant='h3'
            component='h3'
            className={styles.documentViewText}
            onClick={() => {
              // The workflow lives in the URL (?workflow=...); clearing it keeps the other params
              setWorkflow(null);
            }}
          >
            {t('headers.workflows')}
          </Typography>
          <Typography variant='body2' className={styles.separator}>
            ›
          </Typography>
          <Typography variant='body2' className={styles.documentViewText}>
            {workflow}
          </Typography>
        </Box>
        <LibraryList
          cardView={true}
          category={activeTab}
          libraryItems={[...templates]
            .filter(
              (el) =>
                'category' in el &&
                el.category === activeTab &&
                'dl_workflows' in el &&
                el.dl_workflows?.includes(workflow)
            )
            .sort((a, b) => a.name.localeCompare(b.name))}
        />
      </>
    );
  }
};
